import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

export interface ErrorBoundaryProps {
  onComponentCatch: (error: Error, errorInfo: ErrorInfo) => void;
  errorFallback:
    | ReactNode
    | ((hasError: boolean, reset: () => void) => ReactNode);
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static defaultProps = {
    onComponentCatch: () => {},
    errorFallback: <div />,
  };

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  state: ErrorBoundaryState = {
    hasError: false,
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onComponentCatch(error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false });
  };

  render() {
    const { errorFallback, children } = this.props;
    const { hasError } = this.state;
    if (hasError) {
      return typeof errorFallback === 'function'
        ? errorFallback(hasError, this.reset)
        : errorFallback;
    }

    return children;
  }
}
